import { useInfiniteQuery } from '@tanstack/react-query'
import { Empty, Flex, SelectProps, Spin, Select } from 'antd'
import { useState } from 'react'

import { useStocksServiceGetMeasurementUnitListEndpointKey } from 'src/api/queries'
import {
  GetMeasurementUnitListResponseItem,
  StocksService
} from 'src/api/requests'
import { DEFAULT_PER_PAGE } from 'src/app-constants'
import { useDebounce } from 'src/hooks'
import { useLocales } from 'src/locales'

export const MeasurementUnitsSelect = (props: SelectProps) => {
  const { currentLang } = useLocales()
  const [search, setSearch] = useState('')
  const debouncedSearch = useDebounce(search)

  const { data, fetchNextPage, isFetching } = useInfiniteQuery({
    queryKey: [
      useStocksServiceGetMeasurementUnitListEndpointKey,
      debouncedSearch
    ],
    queryFn: async ({ pageParam = 1 }: { pageParam?: number }) => {
      const response = await StocksService.getMeasurementUnitListEndpoint(
        pageParam,
        DEFAULT_PER_PAGE,
        debouncedSearch !== '' ? debouncedSearch : undefined
      )

      return {
        data: response.data,
        total_pages: response.totalPages ?? 0,
        current_page: pageParam
      }
    },
    getNextPageParam: (lastPage: {
      data: GetMeasurementUnitListResponseItem[] | undefined
      total_pages: number
      current_page: number
    }) =>
      lastPage?.current_page < lastPage?.total_pages
        ? lastPage.current_page + 1
        : undefined,
    initialPageParam: 1
  })

  const options = data?.pages.flatMap(
    (page) =>
      page.data?.map((item: GetMeasurementUnitListResponseItem) => ({
        value: item.id,
        label: item.name?.[currentLang?.value]
      })) || []
  )

  return (
    <Select
      allowClear
      popupMatchSelectWidth={true}
      filterOption={false}
      options={options}
      onSearch={(value) => setSearch(value)}
      onClear={() => setSearch('')}
      loading={isFetching}
      showSearch={true}
      onPopupScroll={(e) => {
        const target = e.target as HTMLElement
        if (target.scrollTop + target.offsetHeight === target.scrollHeight)
          fetchNextPage()
      }}
      notFoundContent={
        isFetching ? (
          <Flex justify="center">
            <Spin spinning />
          </Flex>
        ) : (
          <Empty />
        )
      }
      {...props}
    />
  )
}
